import { useEffect, useState } from 'preact/hooks';
import {
  getRecord,
  getRecordWithLinks,
  listAllRecordsWithLinks,
  MAX_LINK_PAGE,
  type LinkRow,
  type RecordRow,
} from '../lib/api';
import { routePath } from '../routes/route';
import { openRecordEditor, openCreateMenu, useRecordsVersion } from '../lib/recordEditor';
import { describeReadFailure, type ReadFailure } from '../lib/readFailure';
import './SiteDetail.css';

// S4 — THE SITE SCREEN (wf02 §03, site()): "The thing a tech needs at 7am
// is HOW TO GET IN — the loading dock, the gate code, the hours they will
// actually be let in, and who to ask for. So access notes sit above the
// work history, and they are the one field on this screen the field can
// edit freely." Equipment lives at the location, not the client — "a
// walk-in cooler belongs to a building."
//
// Joins rode the site's own link page (50, oldest first — see
// ClientDetail's header for the honesty rule at the cap). Work-order and
// equipment ROW DATA came from the wholesale paged lists (the S1 idiom),
// cross-referenced by the link-derived id sets.
//
// ---------------------------------------------------------------------
// S16 — THE '+' AND THE '—' STOP BEING GUESSES.
//
// A location with more than fifty links had its work history quietly cut at
// fifty (oldest first, so the RECENT jobs were the missing ones), showed a
// '—' where the outstanding total belongs, and hung a '+' on every count —
// all of it inferred from `links.length >= 50`, this file's own copy of a
// server constant. Nothing on screen was wrong exactly; nothing was
// knowable either.
//
// The work orders and the equipment now come from THEIR OWN side, with the
// one link each carries back to this location (S15's `?include=links`), so
// neither list can be shortened by this record's link page:
//
//   the orders     hvac_work_order + [wo_site]
//   the equipment  hvac_equipment  + [site]
//
// The site's own links are still read — for one thing, the master_detail
// `client` parent — but at `link_limit=200` and with the server's own
// `links_truncated` reported as a FACT rather than mirrored from a
// constant. Which leaves exactly one honest gap and it is a new one: if
// that page really is cut short and the client link is not in it, the
// screen says the client could not be read, instead of quietly rendering a
// location that belongs to nobody.
//
// The client read is also no longer inside the same try as everything else.
// It was: a refused read of the CLIENT blanked the whole location — access
// notes, work history and all — which is the exact shape of failure this
// tranche exists to end.

export interface SiteDetailProps {
  tenantId: string;
  id: string;
  onNavigate: (path: string) => void;
}

type LoadState = 'loading' | 'ready' | 'error';

const WO_TYPE = 'hvac_work_order';
const EQUIPMENT_TYPE = 'hvac_equipment';
/** The one relationship each side carries back to this location. */
const WO_LINK_TYPES = ['wo_site'];
const EQUIPMENT_LINK_TYPES = ['site'];
/** hvac_site's own master_detail parent. */
const CLIENT_LINK = 'client';

const CLOSED_STATES = new Set(['done', 'cancelled', 'deposited']);

/** What this screen knows about the location's client. Four states, because
 *  "still reading", "there is no parent link", "we could not read it" and
 *  "here it is" are four different things — and a site with no client is a
 *  malformed row worth seeing, not a blank crumb. */
type Parent =
  | { kind: 'known'; row: RecordRow }
  | { kind: 'none' }
  | { kind: 'unread' }
  | { kind: 'pending' };

function money(n: number): string {
  return n.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function humanize(key: string): string {
  return key.replace(/_/g, ' ');
}

function relativeWhen(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const days = Math.round((Date.now() - then) / 86_400_000);
  if (days < 1) return 'today';
  if (days === 1) return '1d';
  return `${days}d`;
}

function textField(r: RecordRow, name: string): string {
  const v = r.fields[name];
  return typeof v === 'string' ? v : '';
}

function woTotal(r: RecordRow): number {
  const v = Number(r.fields['invoice_total_sum'] ?? 0);
  return Number.isFinite(v) ? v : 0;
}

/** The other end of a relationship, whichever side of the link row this
 *  record sits on. */
function linkedId(links: LinkRow[], selfId: string, linkType: string): string | null {
  for (const l of links) {
    if (l.link_type !== linkType) continue;
    if (l.src_id === selfId) return l.dst_id;
    if (l.dst_id === selfId) return l.src_id;
  }
  return null;
}

export function SiteDetail({ tenantId, id, onNavigate }: SiteDetailProps) {
  const [load, setLoad] = useState<LoadState>('loading');
  const [failure, setFailure] = useState<ReadFailure | null>(null);
  const [site, setSite] = useState<RecordRow | null>(null);
  const [parent, setParent] = useState<Parent>({ kind: 'pending' });
  const [woRows, setWoRows] = useState<RecordRow[]>([]);
  const [equipmentRows, setEquipmentRows] = useState<RecordRow[]>([]);
  /** Rows of either kind the server would not answer links for: their
   *  membership of this location cannot be decided, so they are left out of
   *  both lists and counted here instead of silently vanishing. */
  const [unread, setUnread] = useState(0);
  const [retryNonce, setRetryNonce] = useState(0);
  const recordsVersion = useRecordsVersion();

  useEffect(() => {
    let cancelled = false;
    async function loadAll() {
      setLoad('loading');
      setFailure(null);
      setParent({ kind: 'pending' });
      try {
        const [self, orderLinks, equipmentLinks] = await Promise.all([
          getRecordWithLinks(tenantId, id, { linkLimit: MAX_LINK_PAGE }),
          listAllRecordsWithLinks(tenantId, WO_TYPE, { linkTypes: WO_LINK_TYPES }),
          listAllRecordsWithLinks(tenantId, EQUIPMENT_TYPE, { linkTypes: EQUIPMENT_LINK_TYPES }),
        ]);
        if (cancelled) return;

        const unansweredIn = (batch: { unreadable: string[]; truncated: string[] }) =>
          new Set([...batch.unreadable, ...batch.truncated]);
        const orderUnanswered = unansweredIn(orderLinks);
        const equipmentUnanswered = unansweredIn(equipmentLinks);

        const here = (
          batch: { records: RecordRow[]; linksByRecordId: Record<string, LinkRow[]> },
          unanswered: Set<string>,
          linkType: string
        ): RecordRow[] =>
          batch.records.filter((r) => {
            if (unanswered.has(r.id)) return false;
            const links = batch.linksByRecordId[r.id];
            if (links === undefined) return false;
            return linkedId(links, r.id, linkType) === id;
          });

        setSite(self.record);
        setWoRows(
          here(orderLinks, orderUnanswered, 'wo_site').sort((a, b) =>
            // Newest first — the same honest order the WO list holds.
            a.created_at < b.created_at ? 1 : -1
          )
        );
        setEquipmentRows(here(equipmentLinks, equipmentUnanswered, 'site'));
        setUnread(
          orderLinks.records.filter((r) => orderUnanswered.has(r.id)).length +
            equipmentLinks.records.filter((r) => equipmentUnanswered.has(r.id)).length
        );
        setLoad('ready');

        // THE PARENT, ON ITS OWN. A site has no existence apart from its
        // client, so this link is present on a well-formed row — but a
        // client this reader may not see, or a link page that really was cut
        // short, must cost the CRUMB and nothing else. It used to cost the
        // whole location.
        const clientId = linkedId(self.links, self.record.id, CLIENT_LINK);
        if (clientId === null) {
          setParent(self.linksTruncated ? { kind: 'unread' } : { kind: 'none' });
          return;
        }
        try {
          const row = await getRecord(tenantId, clientId);
          if (!cancelled) setParent({ kind: 'known', row });
        } catch {
          if (!cancelled) setParent({ kind: 'unread' });
        }
      } catch (err) {
        if (cancelled) return;
        setFailure(describeReadFailure(err));
        setLoad('error');
      }
    }
    void loadAll();
    return () => {
      cancelled = true;
    };
  }, [tenantId, id, recordsVersion, retryNonce]);

  const crumb = (parentState: Parent, current: string) => (
    <nav class="site-detail__crumb" aria-label="Breadcrumb">
      <button type="button" onClick={() => onNavigate(routePath({ screen: 'today' }))}>
        Ether Inc
      </button>
      <span class="site-detail__crumb-sep" aria-hidden="true">
        ›
      </span>
      <button type="button" onClick={() => onNavigate(routePath({ screen: 'clients' }))}>
        Clients
      </button>
      {parentState.kind === 'known' ? (
        <>
          <span class="site-detail__crumb-sep" aria-hidden="true">
            ›
          </span>
          <button
            type="button"
            onClick={() => onNavigate(routePath({ screen: 'clientDetail', id: parentState.row.id }))}
          >
            {parentState.row.display_name}
          </button>
        </>
      ) : parentState.kind === 'unread' ? (
        // A crumb that is missing reads as "this location has no client",
        // which is a claim. This says the true thing instead, and stays
        // un-tappable because there is nowhere honest to go.
        <>
          <span class="site-detail__crumb-sep" aria-hidden="true">
            ›
          </span>
          <span class="site-detail__crumb-cur">couldn't read the client</span>
        </>
      ) : null}
      <span class="site-detail__crumb-sep" aria-hidden="true">
        ›
      </span>
      <span class="site-detail__crumb-cur">{current}</span>
    </nav>
  );

  if (load === 'loading') {
    return (
      <div class="site-detail">
        {crumb({ kind: 'pending' }, '…')}
        <div class="site-detail__say">One moment…</div>
      </div>
    );
  }

  if (load === 'error' && failure !== null) {
    return (
      <div class="site-detail">
        {crumb({ kind: 'pending' }, '…')}
        <div class="site-detail__say">
          {failure.headline}
          <br />
          <span class="site-detail__say-soft">{failure.detail}</span>
        </div>
        {failure.retryable ? (
          <button type="button" class="site-detail__retry" onClick={() => setRetryNonce((n) => n + 1)}>
            Read it again
          </button>
        ) : null}
      </div>
    );
  }

  if (!site) return null;

  const client = parent.kind === 'known' ? parent.row : null;
  const outstanding = woRows
    .filter((w) => w.lifecycle_state === 'awaiting_payment')
    .reduce((sum, w) => sum + woTotal(w), 0);

  const access = textField(site, 'access_notes');
  const address = textField(site, 'service_address');
  const contactName = textField(site, 'on_site_contact_name');
  const contactPhone = textField(site, 'on_site_contact_phone');
  const contactCount = contactName || contactPhone ? 1 : 0;
  // S16: the '+' is now a fact about ROWS THE SERVER WOULD NOT ANSWER FOR,
  // not an inference from a fifty-long array. Normally there are none and
  // every count on this screen is exact.
  const plus = unread > 0 ? '+' : '';
  const clientId = client?.id;

  return (
    <div class="site-detail">
      {crumb(parent, site.display_name)}

      <div class="site-detail__hero">
        <div>
          <div class="site-detail__eyebrow">
            Location
            {client
              ? ` · ${client.display_name}`
              : parent.kind === 'unread'
                ? " · couldn't read the client"
                : ''}
          </div>
          <h2 class="site-detail__title">{site.display_name}</h2>
          {address ? <div class="site-detail__sub">{address}</div> : null}
        </div>
        <button
          type="button"
          class="site-detail__edit"
          onClick={() => openRecordEditor({ mode: 'edit', typeKey: 'hvac_site', id })}
        >
          Edit
        </button>
      </div>

      <div class="site-detail__cols">
        <div>
          <div class="site-detail__sec">
            <span>Getting in</span>
            <span class="site-detail__mini">techs may edit</span>
          </div>
          {access ? (
            <p class="site-detail__access">{access}</p>
          ) : (
            <div class="site-detail__empty">No access notes yet.</div>
          )}

          <div class="site-detail__sec site-detail__sec--later">
            <span>Work orders here</span>
            <span class="site-detail__mini">
              {woRows.length}
              {plus}
            </span>
          </div>
          {woRows.length === 0 ? (
            <div class="site-detail__empty">No work orders here yet.</div>
          ) : (
            woRows.map((w) => (
              <button
                type="button"
                class="site-detail__row"
                key={w.id}
                onClick={() => onNavigate(routePath({ screen: 'workOrderDetail', id: w.id }))}
              >
                <span
                  class="site-detail__stripe"
                  style={{
                    background:
                      w.fields['urgent'] === true && !CLOSED_STATES.has(w.lifecycle_state ?? '')
                        ? 'var(--status-critical)'
                        : w.lifecycle_state === 'awaiting_payment'
                          ? 'var(--status-warning)'
                          : 'transparent',
                  }}
                />
                <span class="site-detail__row-main">
                  <b>{w.display_name}</b>
                  <small>
                    {humanize(w.lifecycle_state ?? '')} · {relativeWhen(w.created_at)}
                  </small>
                </span>
                <span class="site-detail__row-right">
                  {woTotal(w) > 0 ? `$${money(woTotal(w))}` : '—'}
                </span>
              </button>
            ))
          )}

          <div class="site-detail__sec site-detail__sec--later">
            <span>Equipment</span>
            <span class="site-detail__mini">
              {equipmentRows.length}
              {plus}
            </span>
          </div>
          {equipmentRows.length === 0 ? (
            <div class="site-detail__empty">
              <b class="site-detail__empty-lead">No equipment recorded here yet</b>
              Photograph a nameplate on the next visit and it lands here.
            </div>
          ) : (
            equipmentRows.map((e) => (
              <button
                type="button"
                class="site-detail__row"
                key={e.id}
                onClick={() => openRecordEditor({ mode: 'edit', typeKey: 'hvac_equipment', id: e.id })}
              >
                <span class="site-detail__stripe" />
                <span class="site-detail__row-main">
                  <b>{e.display_name}</b>
                  {textField(e, 'make') || textField(e, 'model') ? (
                    <small>{[textField(e, 'make'), textField(e, 'model')].filter(Boolean).join(' · ')}</small>
                  ) : null}
                </span>
                <span class="site-detail__row-right">
                  {textField(e, 'last_service_date') ? (
                    <span class="site-detail__row-st">last service {textField(e, 'last_service_date')}</span>
                  ) : null}
                </span>
              </button>
            ))
          )}
        </div>

        <div>
          <div class="site-detail__sec">
            <span>Outstanding here</span>
          </div>
          <div
            class="site-detail__big"
            style={{ color: outstanding > 0 ? 'var(--status-critical)' : 'var(--ink)' }}
          >
            {unread > 0 ? '—' : `$${money(outstanding)}`}
          </div>
          {unread > 0 ? (
            // Kept, and re-anchored: a location's history is no longer
            // "more than one read can count" — it is read whole. This shows
            // only when the SERVER declined to answer for specific rows, and
            // says how many rather than implying the whole history is
            // beyond reach.
            <div class="site-detail__soft">
              {unread === 1 ? 'One record' : `${unread} records`} could not be read, so a total here
              would be an undercount.
            </div>
          ) : null}

          <div class="site-detail__sec site-detail__sec--later">
            <span>On site</span>
          </div>
          {contactCount === 0 ? (
            <div class="site-detail__empty">No on-site contact recorded yet.</div>
          ) : (
            <div class="site-detail__kv">
              <span>{contactName || 'Contact'}</span>
              <b class="site-detail__kv-soft">{contactPhone || '—'}</b>
            </div>
          )}

          <div class="site-detail__sec site-detail__sec--later">
            <span>Actions</span>
          </div>
          <div class="site-detail__pills">
            <button
              type="button"
              class="site-detail__pill"
              onClick={() =>
                openRecordEditor({
                  mode: 'create',
                  typeKey: 'hvac_work_order',
                  prefillRels: {
                    wo_site: [id],
                    ...(clientId ? { wo_client: [clientId] } : {}),
                  },
                })
              }
            >
              New work order
            </button>
            <button
              type="button"
              class="site-detail__pill"
              onClick={() =>
                openRecordEditor({
                  mode: 'create',
                  typeKey: 'hvac_equipment',
                  prefillRels: {
                    site: [id],
                    ...(clientId ? { equipment_client: [clientId] } : {}),
                  },
                })
              }
            >
              Add equipment
            </button>
            {address ? (
              <a
                class="site-detail__pill site-detail__pill--link"
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
                target="_blank"
                rel="noreferrer"
              >
                Directions
              </a>
            ) : null}
          </div>
        </div>
      </div>

      <div class="site-detail__rail">
        <span class="site-detail__rail-label">At this location</span>
        <span class="site-detail__pill site-detail__pill--fact site-detail__pill--on">
          <b>
            {woRows.length}
            {plus}
          </b>{' '}
          Work orders
        </span>
        <span class="site-detail__pill site-detail__pill--fact">
          <b>
            {equipmentRows.length}
            {plus}
          </b>{' '}
          Equipment
        </span>
        <span class="site-detail__pill site-detail__pill--fact">
          <b>{contactCount}</b> Contacts
        </span>
        <button type="button" class="site-detail__pill site-detail__pill--add" onClick={openCreateMenu}>
          + Add
        </button>
      </div>
    </div>
  );
}
